import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { guardarCredenciales, leerCredenciales } from '../api/cliente'
import { comprobarConexion, ultimaActualizacion } from '../api/consultas'
import Avisos from '../componentes/Avisos'
import { guardarUsarMejora, usarMejora } from '../api/mejora'
import Historial from '../componentes/Historial'
import Salud from '../componentes/Salud'
import { almacen, formatearBytes, pedirAlmacenamientoPersistente } from '../lib/almacen'
import { activar, desactivar, estaActivado, hayFaceId } from '../lib/biometria'
import {
  aplicarFondo,
  aplicarTema,
  FONDOS,
  leerFondo,
  leerTema,
  TEMAS,
  type Fondo,
  type Tema,
} from '../lib/tema'

export default function Ajustes() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['conexion'],
    queryFn: comprobarConexion,
  })

  const { data: ultima } = useQuery({
    queryKey: ['ultimaActualizacion'],
    queryFn: ultimaActualizacion,
  })

  const [ocupado, setOcupado] = useState<number | null>(null)
  const [persistente, setPersistente] = useState<boolean | null>(null)
  const [instalada, setInstalada] = useState(false)
  const [faceIdPosible, setFaceIdPosible] = useState(false)
  const [faceIdPuesto, setFaceIdPuesto] = useState(estaActivado)
  const [falloFaceId, setFalloFaceId] = useState<string | null>(null)
  const [tema, setTema] = useState<Tema>(leerTema)
  const [fondo, setFondo] = useState<Fondo>(leerFondo)
  const [verHistorial, setVerHistorial] = useState(false)
  const [mejora, setMejora] = useState(usarMejora)

  const credenciales = leerCredenciales()

  useEffect(() => {
    void almacen.ocupado().then(setOcupado)
    void navigator.storage?.persisted?.().then(setPersistente)
    void hayFaceId().then(setFaceIdPosible)
    // iOS no expone de otra forma si es una webapp instalada
    setInstalada(window.matchMedia('(display-mode: standalone)').matches)
  }, [])

  async function activarFaceId() {
    setFalloFaceId(null)
    try {
      await activar(credenciales?.usuario ?? 'yomou')
      setFaceIdPuesto(true)
    } catch {
      // Cancelarlo cae aqui igual que un fallo de verdad
      setFalloFaceId('No se pudo activar. Vuelve a intentarlo.')
    }
  }

  function quitarFaceId() {
    desactivar()
    setFaceIdPuesto(false)
  }

  function cambiarTema(nuevo: Tema) {
    aplicarTema(nuevo)
    setTema(nuevo)
  }

  function cambiarFondo(nuevo: Fondo) {
    aplicarFondo(nuevo)
    setFondo(nuevo)
  }

  async function activarPersistencia() {
    setPersistente(await pedirAlmacenamientoPersistente())
  }

  function cerrarSesion() {
    if (!confirm('Se borraran tus credenciales de este dispositivo. Las descargas se conservan.')) {
      return
    }
    guardarCredenciales(null)
    location.reload()
  }

  async function vaciarCache() {
    if (!confirm('Se borraran las imagenes guardadas. Se volveran a descargar al leer.')) return
    for (const clave of await almacen.claves('img:')) {
      await almacen.borrar(clave)
    }
    // Aqui se vaciaban tambien los `caches` del navegador, y hacia justo lo
    // contrario de lo que dice el boton: las paginas viven en IndexedDB y lo
    // unico que hay en `caches` es el armazon de la app, o sea lo que
    // permite abrirla sin conexion. Se borraba eso y ni una sola imagen.
    setOcupado(await almacen.ocupado())
  }

  return (
    <>
      <h1>Ajustes</h1>
      <p className="subtitulo">Cuenta, servidor y almacenamiento</p>

      <div className="aviso">
        <strong>Sesion</strong>
        {credenciales ? (
          <>
            <p>
              Conectado como <b>{credenciales.usuario}</b>
              <span className="meta">
                Tus credenciales quedan guardadas en este dispositivo. No hace falta volver a
                entrar.
              </span>
            </p>
            <button className="boton secundario" onClick={cerrarSesion}>
              Cerrar sesion
            </button>
          </>
        ) : (
          <p className="meta">Sesion gestionada por el servidor de desarrollo.</p>
        )}
      </div>

      <div className="aviso">
        <strong>Apariencia</strong>

        <p className="meta">Tema de la app</p>
        <div className="opciones">
          {TEMAS.map((opcion) => (
            <button
              key={opcion.id}
              className={opcion.id === tema ? 'chip activo' : 'chip'}
              onClick={() => cambiarTema(opcion.id)}
            >
              <span className="muestra" style={{ background: opcion.muestra }} />
              {opcion.nombre}
            </button>
          ))}
        </div>
        <p className="meta">{TEMAS.find((o) => o.id === tema)?.nota}</p>

        {/* Va aparte del tema a proposito: es lo que rodea a la pagina cuando
            no llena la pantalla, y lo normal es quererlo negro aunque la app
            este en claro. */}
        <p className="meta">Fondo del lector</p>
        <div className="opciones">
          {FONDOS.map((opcion) => (
            <button
              key={opcion.id}
              className={opcion.id === fondo ? 'chip activo' : 'chip'}
              onClick={() => cambiarFondo(opcion.id)}
            >
              <span className="muestra" style={{ background: opcion.muestra }} />
              {opcion.nombre}
            </button>
          ))}
        </div>
        <p className="meta">{FONDOS.find((o) => o.id === fondo)?.nota}</p>
      </div>

      <Avisos />

      <Salud />

      <div className="aviso">
        <strong>Face ID</strong>
        {!faceIdPosible ? (
          <p className="meta">
            Este dispositivo no ofrece Face ID a la app. En el iPhone hay que abrirla desde el icono
            de la pantalla de inicio, no como pagina de Safari.
          </p>
        ) : faceIdPuesto ? (
          <>
            <p>
              Activado
              <span className="meta">
                Yomou pedira tu cara cada vez que la abras. Funciona sin conexion, porque la
                comprueba el propio iPhone.
              </span>
            </p>
            <button className="boton secundario" onClick={quitarFaceId}>
              Desactivar Face ID
            </button>
          </>
        ) : (
          <>
            <p className="meta">
              Bloquea Yomou con tu cara. Protege tu biblioteca si alguien coge el iPhone; no cambia
              la contrasena del servidor.
            </p>
            {falloFaceId && <p className="error-texto">{falloFaceId}</p>}
            <button className="boton secundario" onClick={activarFaceId}>
              Activar Face ID
            </button>
          </>
        )}
      </div>

      <div className="aviso">
        <strong>Servidor</strong>
        {isLoading && <p>Comprobando…</p>}
        {error && <p className="error-texto">{(error as Error).message}</p>}
        {data && (
          <p>
            Conectado — Suwayomi {data.version}
            <span className="meta">revision {data.revision}</span>
          </p>
        )}
      </div>

      <div className="aviso">
        {/* La version abre el historial: es donde uno va a mirar cuando algo
            se comporta distinto que ayer. */}
        <button type="button" className="version" onClick={() => setVerHistorial(true)}>
          <span>
            <strong>Yomou {__VERSION__}</strong>
            <span className="meta">Compilada el {__SELLO__}</span>
          </span>
          <span className="version-mas">Ver los cambios ›</span>
        </button>
      </div>
      {verHistorial && <Historial alCerrar={() => setVerHistorial(false)} />}

      <div className="aviso">
        <strong>Imagenes mejoradas</strong>
        <p className="meta">
          Los capitulos que marcas con ✨ en la ficha de la serie se procesan en el servidor y
          se ven al doble de tamano. Aqui se apagan para todo el aparato, por si quieres
          comparar o ahorrar datos: una pagina mejorada pesa el doble.
        </p>
        <div className="opciones">
          {[
            { id: true, nombre: 'Usarlas' },
            { id: false, nombre: 'No usarlas' },
          ].map((opcion) => (
            <button
              key={String(opcion.id)}
              className={opcion.id === mejora ? 'chip activo' : 'chip'}
              onClick={() => {
                guardarUsarMejora(opcion.id)
                setMejora(opcion.id)
              }}
            >
              {opcion.nombre}
            </button>
          ))}
        </div>
      </div>

      <div className="aviso">
        <strong>Actualizaciones</strong>
        <p>
          El servidor busca capitulos nuevos cada 6 horas
          <span className="meta">
            Ultima comprobacion: {ultima ? new Date(ultima).toLocaleString('es-ES') : 'nunca'}
          </span>
        </p>
        <p className="meta">
          Los capitulos nuevos se descargan solos al servidor (hasta 5 por serie y comprobacion).
          Puedes forzar la comprobacion desde el boton ↻ de la Biblioteca.
        </p>
      </div>

      <div className="aviso">
        <strong>Almacenamiento</strong>
        <p>
          Ocupado por Yomou: {ocupado === null ? '…' : formatearBytes(ocupado)}
          <span className="meta">
            {instalada ? 'App instalada en la pantalla de inicio.' : 'Abierta como pagina web.'}
          </span>
        </p>

        {persistente ? (
          <p className="meta">
            Almacenamiento protegido. iOS no borrara tus datos ni tu sesion.
          </p>
        ) : (
          <>
            <p className="meta">
              {instalada
                ? 'Sin proteger: iOS podria borrar tus datos si al iPhone le falta espacio.'
                : 'Anade Yomou a la pantalla de inicio desde Safari. Como pagina normal, iOS borra los datos a los 7 dias sin usarla.'}
            </p>
            <button className="boton secundario" onClick={activarPersistencia}>
              Proteger datos
            </button>
          </>
        )}

        <div style={{ marginTop: 14 }}>
          <button className="boton secundario" onClick={vaciarCache}>
            Vaciar cache de imagenes
          </button>
        </div>
      </div>
    </>
  )
}
